import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { Box, X } from 'lucide-react';
import type {
  ArtModelRecipe,
  ArtModelRecord,
  ArtModelRole,
  ArtModelSpec,
} from '@volley/crucible-ipc-types';
import { useCrucibleClient } from '../../transport/context.js';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog.js';

export interface AddModelDialogProps {
  gameId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: ReactElement;
  onAdded: (record: ArtModelRecord) => void;
}

const RECIPES: ReadonlyArray<{ value: ArtModelRecipe; label: string }> = [
  { value: 'static-prop', label: 'Static prop' },
  { value: 'character', label: 'Character' },
  { value: 'repair-textures', label: 'Repair textures' },
];

function roleFor(recipe: ArtModelRecipe, animations: string[]): ArtModelRole {
  if (recipe !== 'character') return 'textured-model';
  return animations.length > 0 ? 'animated-model' : 'rigged-model';
}

function normalizeAnimations(value: string): string[] {
  return value
    .split(',')
    .map((animation) => animation.trim())
    .filter(Boolean);
}

export function AddModelDialog({
  gameId,
  open,
  onOpenChange,
  trigger,
  onAdded,
}: AddModelDialogProps): React.JSX.Element {
  const client = useCrucibleClient();
  const idInputRef = useRef<HTMLInputElement>(null);
  const [id, setId] = useState('');
  const [prompt, setPrompt] = useState('');
  const [recipe, setRecipe] = useState<ArtModelRecipe>('static-prop');
  const [sourceAssetId, setSourceAssetId] = useState('');
  const [animationText, setAnimationText] = useState('');
  const [topology, setTopology] = useState(true);
  const [uvUnwrap, setUvUnwrap] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const normalizedId = id.trim();
  const animations = useMemo(() => normalizeAnimations(animationText), [animationText]);
  const canSubmit =
    normalizedId.length > 0 &&
    prompt.trim().length > 0 &&
    (recipe !== 'repair-textures' || sourceAssetId.trim().length > 0);

  useEffect(() => {
    if (open) return;
    setId('');
    setPrompt('');
    setRecipe('static-prop');
    setSourceAssetId('');
    setAnimationText('');
    setTopology(true);
    setUvUnwrap(true);
    setError(null);
  }, [open]);

  const addModel = async (): Promise<void> => {
    if (!canSubmit || busy) return;
    const spec: ArtModelSpec = {
      id: normalizedId,
      stage: 2,
      prompt: prompt.trim(),
      recipe,
      role: roleFor(recipe, animations),
      format: 'glb',
      recipeOptions: { topology, uvUnwrap },
      animations,
      exportAs: `${normalizedId}.glb`,
      exportDir: 'models',
      ...(recipe === 'repair-textures' ? { sourceAssetId: sourceAssetId.trim() } : {}),
    };

    setBusy(true);
    setError(null);
    try {
      const result = await client.invoke('art:model:add', { gameId, spec });
      onAdded(result.record);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!busy) onOpenChange(nextOpen);
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        showClose={false}
        aria-describedby={undefined}
        data-testid="add-model-dialog"
        className="w-[min(92vw,512px)] max-h-[min(720px,90vh)] bg-crucible-bg"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          idInputRef.current?.focus();
        }}
      >
        <header className="mb-5 flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <Box aria-hidden="true" className="h-4 w-4 shrink-0 text-crucible-accent" />
            <DialogTitle className="truncate text-sm font-semibold text-white/90">
              Add 3D model
            </DialogTitle>
          </div>
          <DialogClose asChild>
            <button
              type="button"
              disabled={busy}
              aria-label="Close"
              title="Close"
              className="grid h-9 w-9 shrink-0 place-items-center rounded-md text-white/45 transition hover:bg-white/10 hover:text-white/80 disabled:opacity-40"
            >
              <X aria-hidden="true" className="h-4 w-4" />
            </button>
          </DialogClose>
        </header>

        {error && (
          <div
            role="alert"
            className="mb-4 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300"
          >
            {error}
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="flex min-w-0 flex-col gap-1 text-xs text-white/60">
            Model id
            <input
              ref={idInputRef}
              value={id}
              onChange={(event) => setId(event.target.value)}
              disabled={busy}
              data-testid="add-model-id"
              placeholder="weathered-crate"
              className="h-10 rounded-md border border-white/15 bg-black/40 px-3 text-sm text-white/90 placeholder:text-white/25 focus:border-crucible-accent focus:outline-none disabled:opacity-50"
            />
          </label>
          <label className="flex min-w-0 flex-col gap-1 text-xs text-white/60">
            Pipeline
            <select
              value={recipe}
              onChange={(event) => setRecipe(event.target.value as ArtModelRecipe)}
              disabled={busy}
              data-testid="add-model-recipe"
              className="h-10 rounded-md border border-white/15 bg-black/40 px-3 text-sm text-white/90 focus:border-crucible-accent focus:outline-none disabled:opacity-50"
            >
              {RECIPES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="mt-4 flex flex-col gap-1 text-xs text-white/60">
          Prompt
          <textarea
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            disabled={busy}
            data-testid="add-model-prompt"
            placeholder="A weathered shipping crate with painted markings"
            className="h-24 resize-none rounded-md border border-white/15 bg-black/40 p-3 text-sm text-white/90 placeholder:text-white/25 focus:border-crucible-accent focus:outline-none disabled:opacity-50"
          />
        </label>

        {recipe === 'repair-textures' && (
          <label className="mt-4 flex flex-col gap-1 text-xs text-white/60">
            Source asset id
            <input
              value={sourceAssetId}
              onChange={(event) => setSourceAssetId(event.target.value)}
              disabled={busy}
              data-testid="add-model-source-asset"
              className="h-10 rounded-md border border-white/15 bg-black/40 px-3 text-sm text-white/90 focus:border-crucible-accent focus:outline-none disabled:opacity-50"
            />
          </label>
        )}

        {recipe === 'character' && (
          <label className="mt-4 flex flex-col gap-1 text-xs text-white/60">
            Animations
            <input
              value={animationText}
              onChange={(event) => setAnimationText(event.target.value)}
              disabled={busy}
              data-testid="add-model-animations"
              placeholder="idle, walk, wave"
              className="h-10 rounded-md border border-white/15 bg-black/40 px-3 text-sm text-white/90 placeholder:text-white/25 focus:border-crucible-accent focus:outline-none disabled:opacity-50"
            />
          </label>
        )}

        <fieldset className="mt-4 grid gap-2 border-0 p-0 sm:grid-cols-2">
          <legend className="mb-2 text-xs text-white/60">Geometry preparation</legend>
          <label className="flex min-h-10 items-center gap-2 rounded-md border border-white/10 bg-white/5 px-3 text-xs text-white/75">
            <input
              type="checkbox"
              checked={topology}
              onChange={(event) => setTopology(event.target.checked)}
              disabled={busy}
              className="accent-crucible-accent"
            />
            Optimize topology
          </label>
          <label className="flex min-h-10 items-center gap-2 rounded-md border border-white/10 bg-white/5 px-3 text-xs text-white/75">
            <input
              type="checkbox"
              checked={uvUnwrap}
              onChange={(event) => setUvUnwrap(event.target.checked)}
              disabled={busy}
              className="accent-crucible-accent"
            />
            Unwrap UVs
          </label>
        </fieldset>

        <footer className="mt-5 flex justify-end gap-2">
          <DialogClose asChild>
            <button
              type="button"
              disabled={busy}
              className="h-10 rounded-md px-3 text-xs font-medium text-white/60 transition hover:bg-white/10 hover:text-white/85 disabled:opacity-40"
            >
              Cancel
            </button>
          </DialogClose>
          <button
            type="button"
            onClick={() => void addModel()}
            disabled={busy || !canSubmit}
            data-testid="add-model-submit"
            className="h-10 rounded-md bg-crucible-accent px-4 text-xs font-semibold text-black transition hover:brightness-110 disabled:cursor-default disabled:opacity-40"
          >
            {busy ? 'Adding…' : 'Add model'}
          </button>
        </footer>
      </DialogContent>
    </Dialog>
  );
}
